'use client';

import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function BotoksunDuygusalEtkileriPage() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/botoksun-duygusal-etkileri';
  const pageTitle = 'Botoksun Duygusal Etkileri';
  const pageDescription = 'Botoks yalnızca kırışıklıkları değil, duyguların yüzümüzdeki izlerini de etkiler. Peki estetik bir işlem, insanın iç dünyasında neleri değiştirir?';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Botoksun Duygusal Etkileri', href: '/tr/blog/botoksun-duygusal-etkileri' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Botoksun duygusal etkileri ve psikolojik yansımaları değerlendirmesi"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-rose-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Psikolojik Etki
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Botoksun Duygusal Etkileri
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>8 Şubat 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>8 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Botoks denilince akla ilk gelen "kırışıklıkları yok etmek" olur. Oysa botoks, yalnızca fiziksel bir değişim değil; aynı zamanda yüz ifadesiyle ruh hali arasındaki köprüyü de etkileyen bir dönüşümdür.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Bu yazıda botoksun "mecazi" yani duygusal yansımalarına, psikolojik denge ve özgüven üzerindeki etkilerine değineceğiz.
              </p>

              <div className="bg-purple-50 rounded-xl p-6 mb-8">
                <p className="text-purple-800 font-medium">
                  💡 Kısacası: Botoks, bazen alnı değil — zihni de rahatlatır.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1. Yüz ve Duygu Arasındaki Bilimsel Bağlantı
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                İnsan yüzü, duyguların en görünür yüzeyidir. Psikolojide "Facial Feedback Hypothesis" (Yüz Geri Bildirim Teorisi) denilen bir kavram vardır: Yüz kaslarının hareketi, beynin duygusal merkezini (amigdala) etkiler.
              </p>

              <p className="text-gray-700 mb-4 leading-relaxed">
                Örneğin; sürekli kaşlarını çatan biri farkında olmadan stres hormonlarını artırır. Bu kişi botoks yaptırdığında, kaş çatma refleksi azaldığı için beyin "tehlike geçti" sinyali alır.
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <p className="text-blue-800 font-medium">
                  Sonuç olarak kişi yalnızca "daha genç" görünmez — aynı zamanda daha huzurlu hisseder.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. Gerginliği Dondurmak: Sakinliğin Estetik Hali
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Botoksun temel etkisi, kas kasılmalarını azaltmaktır. Ancak mecazi olarak bu etki, "yüzdeki gerginliği dondurmak" değil, ruhtaki gerginliği gevşetmek anlamına gelir.
              </p>

              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-green-800 space-y-2">
                  <li>Kaş arasındaki öfke çizgileri azaldığında, kişi daha yumuşak bir ifade kazanır.</li>
                  <li>Aynadaki yüz, artık "kızgın" değil "dingin" görünür.</li>
                  <li>Bu durum sosyal ilişkilerde bile karşılıklı etkileşimi değiştirir.</li>
                </ul>
                <p className="text-green-800 mt-3 font-medium">
                  💬 Yani botoks, bazen bir mimik kasından çok bir anıyı serbest bırakır.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. Gülümsemeyi Durdurmaz, Dengeler
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Botoksun "donuk ifade" yarattığı düşünülür, ancak doğru uygulamada durum tam tersidir. Doğru doz ve teknikle yapıldığında botoks, duygusal ifadeyi baskılamaz, yalnızca aşırı mimik tepkilerini dengeler.
              </p>

              <p className="text-gray-700 mb-4 leading-relaxed">
                Bu da özellikle:
              </p>

              <div className="bg-orange-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-orange-800 space-y-2">
                  <li>stresli</li>
                  <li>öfke eğilimli</li>
                  <li>veya aşırı mimik kullanan kişilerde daha kontrollü bir yüz ifadesiyle içsel dengeyi güçlendirir.</li>
                </ul>
                <p className="text-orange-800 mt-3 font-medium">
                  💡 Mecazi anlamda: Botoks, yüzü susturmaz — duygulara netlik kazandırır.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. Estetik Psikoloji ve Özgüven Döngüsü
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Klinik gözlemler, estetik işlemler sonrası özgüven, sosyal etkileşim ve yaşam memnuniyetinde artış olduğunu göstermektedir. Kişi kendini daha genç ve enerjik gördükçe, beynin dopamin (mutluluk) düzeyi artar. Bu da günlük davranışlara pozitif yansır.
              </p>

              <div className="bg-indigo-50 rounded-xl p-6 mb-6">
                <p className="text-indigo-800 font-medium">
                  Botoksun bu yönüyle oluşturduğu etki; 🌀 görünüm → özsaygı → sosyal özgüven → pozitif duygu döngüsü şeklinde işler.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5. Botoksun Duygusal "Yan Etkileri" (Olumlu Anlamda)
              </h2>
              
              {/* Table */}
              <div className="overflow-x-auto mb-6">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Duygusal Etki
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Açıklama
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Sakinlik hissi</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Kas gevşemesiyle stres azalır</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Yenilenme duygusu</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Aynada genç bir ifade görmek moral yükseltir</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Sosyal güven artışı</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Kendinden emin görünüm iletişimi güçlendirir</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Duygusal hafiflik</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Kaş çatmamak = zihinsel gevşeme</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Daha pozitif beden dili</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Yumuşak mimikler daha empatik algılanır</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6. Etik ve Doğallık Dengesi
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Botoksun duygusal etkileri pozitif olsa da, bu etki doğallık sınırını koruduğunda ortaya çıkar. Aşırı botoks uygulamaları kişiyi yabancılaştırabilir; bu nedenle amaç, "donmuş bir yüz" değil, "dengelenmiş bir ifade" olmalıdır.
              </p>

              <div className="bg-rose-50 rounded-xl p-6 mb-8">
                <p className="text-rose-800 font-medium">
                  💬 "Gerginliği değil, huzuru donduran botoks" en sağlıklı uygulamadır.
                </p>
              </div>

              {/* Image placeholder for article */}
              <div className="relative h-64 rounded-xl overflow-hidden mb-8">
                <Image
                  src="/images/terleme-tedavisi.webp"
                  alt="Botoksun duygusal etkileri ve psikolojik yansımaları"
                  fill
                  className="object-cover"
                />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>

              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">1. Botoks gerçekten ruh halini değiştirir mi?</h3>
                  <p className="text-gray-700">Evet. Nöropsikolojik çalışmalar, mimiklerin beyin kimyasını etkilediğini göstermektedir.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">2. Botoks sonrası kendimi daha iyi hissetmem normal mi?</h3>
                  <p className="text-gray-700">Evet, çünkü estetik tatmin beyinde dopamin ve serotonin salınımını artırır.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">3. Botoks beni ifadesiz yapar mı?</h3>
                  <p className="text-gray-700">Hayır, doğru dozda uygulandığında mimikler korunur, yalnızca kas aşırılığı azaltılır.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">4. Duygusal olarak bağımlılık yaratır mı?</h3>
                  <p className="text-gray-700">Fiziksel değil, psikolojik memnuniyet nedeniyle kişi devam etmek isteyebilir.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-8">
                <li>Harvard Health Publishing (2024) – "Facial Feedback Hypothesis and Botox Studies"</li>
                <li>American Psychological Association (APA) – "The Emotional Side of Aesthetic Interventions"</li>
                <li>Türk Estetik Tıp Derneği (TETD) – "Botoksun Psikolojik Etkileri Raporu"</li>
                <li>ISAPS Global Report 2024 – "Patient Satisfaction and Emotional Outcomes After Botulinum Toxin"</li>
              </ul>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-rose-100 text-rose-800 px-3 py-1 rounded-full text-sm">#botoks</span>
                <span className="bg-rose-100 text-rose-800 px-3 py-1 rounded-full text-sm">#duygusaleetkiler</span>
                <span className="bg-rose-100 text-rose-800 px-3 py-1 rounded-full text-sm">#psikoloji</span>
                <span className="bg-rose-100 text-rose-800 px-3 py-1 rounded-full text-sm">#özgüven</span>
                <span className="bg-rose-100 text-rose-800 px-3 py-1 rounded-full text-sm">#yüzifadesi</span>
                <span className="bg-rose-100 text-rose-800 px-3 py-1 rounded-full text-sm">#estetikpsikoloji</span>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-rose-600 to-pink-600 rounded-2xl p-8 text-white text-center mt-12">
              <h3 className="text-2xl font-bold mb-4">Botoks Hakkında Bilgi Alın</h3>
              <p className="mb-6">Botoksun fiziksel ve duygusal etkileri hakkında uzman doktorumuzdan detaylı bilgi alın.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  href={`https://wa.me/905438999586?text=${encodeURIComponent('Botoksun duygusal etkileri hakkında bilgi almak istiyorum.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                >
                  <FaWhatsapp className="w-5 h-5 mr-2" />
                  WhatsApp ile İletişim
                </Link>
                <Link
                  href="/tr/iletisim"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-rose-600 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Randevu Al
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <Link
              href="/tr/blog/botoks-nedir-nasil-yapilir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks Nedir ve Nasıl Yapılır?</h4>
              <p className="text-gray-600 text-sm">Botoksun ne olduğu, nasıl çalıştığı ve uygulama sürecinin ayrıntılı anlatımı.</p>
            </Link>
            <Link
              href="/tr/blog/botoks-yan-etkileri"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoksun Yan Etkileri ve Riskleri</h4>
              <p className="text-gray-600 text-sm">Botoks yan etkileri nelerdir? Riskler nasıl azaltılır?</p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
